import type { FC, ReactElement } from 'react';
import React, { Children, cloneElement, useMemo, isValidElement } from 'react';
import cn from 'classnames';

import type { TAccordionItem, TAccordionHeader, TAccordionPanel } from '../models';
import { useAccordionContext } from '../context';
import type mobileStyles from '../styles/mobile.css';

export const Item: FC<TAccordionItem> = ({
    Stack,
    backgroundColor,
    borderRadius = 32,
    children,
    className,
    id,
    isLastChild,
    isMobile,
    panelKey,
    prevChildBackgroundColor,
    styles,
}): JSX.Element => {
    const { dividerEnabled, openedValue, theme } = useAccordionContext();

    /*
     TODO разобраться с типизацией пропа child (Children.map) в стрелочной ф-ии,
     т.к. JSX.Element было временным решением чтобы продолжить работу команд
     создана таска https://jira.moscow.alfaintra.net/browse/ALFABANKRU-62250
     */

    const isOpenItem = useMemo(() => openedValue?.includes(id), [openedValue, id]);
    const isPositionBeforeOpenedItem = useMemo(
        () => Number(openedValue?.length && openedValue[0]) - 1 === Number(id),
        [openedValue, id]
    );
    const isNewDesign = useMemo(() => theme === 'white' && isMobile, [theme, isMobile]);

    return (
        <Stack>
            {zIndex => (
                <div
                    className={cn(
                        styles.item,
                        {
                            [styles.withOutDivider]: !dividerEnabled,
                            [styles.opened]: isOpenItem,
                            [(styles as typeof mobileStyles).newDesignItem]:
                                isNewDesign && isOpenItem,
                            [(styles as typeof mobileStyles).itemBeforeOpenedItem]:
                                isNewDesign && isPositionBeforeOpenedItem,
                        },
                        className
                    )}
                    data-test-id={`accordion-item-${panelKey}`}
                    style={{
                        zIndex,
                        ...(isNewDesign && { borderRadius: isOpenItem ? borderRadius : 0 }),
                        backgroundColor,
                    }}
                >
                    {Children.map(children, child => {
                        if (isValidElement<TAccordionHeader | TAccordionPanel>(child)) {
                            return cloneElement(child as ReactElement, {
                                id,
                                panelKey,
                                prevChildBackgroundColor,
                                isLastChild,
                            });
                        }
                    })}
                </div>
            )}
        </Stack>
    );
};
